<template>
    <div
    >
        <LineChart
                :data=chartData
                :options=chartOptions
                :height=250
            >
        </LineChart>
        <template v-if="costOfAfterCurrentMonth > 0">
            <p :style="{'color': themeColors.warning }" class="text-right mr-2 mb-0">
                <small>
                    * Current: {{ $t('org.billing.costNotExpressedInGraph').replace('{cost}', $_getDollar(costOfAfterCurrentMonth).replace(' ','')) }}
                </small>
            </p>
        </template>
        <template v-if="costOfAfterPastMonth > 0">
            <p :style="{'color': themeColors.warning }" class="text-right mr-2 mb-0">
                <small>
                    * Last Month: {{ $t('org.billing.costNotExpressedInGraph').replace('{cost}', $_getDollar(costOfAfterPastMonth).replace(' ','')) }}
                </small>
            </p>
        </template>
    </div>
</template>

<script>
import { getCurrentInstance, computed, toRefs } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import CreateVueChart from '@/components/CreateVueChart.js'
import formattedDate from '@/utils/formattedDate'

export default {
    components: {
        LineChart: CreateVueChart('Line')
    },
    props: {
        trends: {
            type: Object,
            default: function () {
                return {
                    pastTrends: [],
                    currentTrends: []
                }
            }
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        },
        estimated: {
            type: Boolean
        },
        status: {
            type: [String, Number],
            default: ''
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy

        const { trends, themeColors, estimated } = toRefs(props)
        const { __toKFormat } = useCommonData()
        const defaultYKeys = ['Last Month', 'Current', 'Estimated Cost']
        const yKeys = computed(() => estimated.value ? defaultYKeys : defaultYKeys.slice(0, 2))
        const lang = computed(() => vm.$i18n.locale)
        const pastTrends = computed(() => trends.value.pastTrends)
        const currentTrends = computed(() => trends.value.currentTrends)
        const pastDate = computed(() => (pastTrends.value.length && pastTrends.value[0].date) || '')
        const currentDate = computed(() => (currentTrends.value.length && currentTrends.value[0].date) || '')
        const pastAfterIndex = computed(() => pastTrends.value.findIndex(item => (pastDate.value.substring(0, 7)) !== (item.date.substring(0, 7))))
        const currentAfterIndex = computed(() => currentTrends.value.findIndex(item => (currentDate.value.substring(0, 7)) !== (item.date.substring(0, 7))))

        const costOfAfterCurrentMonth = computed(() => currentAfterIndex.value > 0 ? currentTrends.value.slice(-1)[0].unCost - (currentTrends.value[currentAfterIndex.value - 1].unCost) : null)
        const costOfAfterPastMonth = computed(() => pastAfterIndex.value > 0 ? pastTrends.value.slice(-1)[0].unCost - (pastTrends.value[pastAfterIndex.value - 1].unCost) : null)
        const getDayFromYMD = (ymd) => Number(ymd.slice(-2))

        const unCostReducer = (accumulator, currentValue) => {
            accumulator[getDayFromYMD(currentValue.date)] = currentValue.unCost
            return accumulator
        }
        const indexOfStartEstimatedCost = computed(() => estimated.value ? currentTrends.value.filter(item => item.unCost !== 0 && item.estimatedCost === 0).length - 1 : daysOfMonth.length)
        const estimatedReducer = (accumulator, currentValue, index) => {
            if (index > indexOfStartEstimatedCost.value) {
                accumulator[getDayFromYMD(currentValue.date)] = currentValue.estimatedCost
            }
            return accumulator
        }
        const pastTrendMap = computed(() => pastTrends.value.slice(0, pastAfterIndex.value > 0 ? pastAfterIndex.value : undefined).reduce(unCostReducer, {}))
        const currentTrendMap = computed(() => currentTrends.value.slice(0, currentAfterIndex.value > 0 ? currentAfterIndex.value : undefined).reduce(unCostReducer, {}))
        const estimatedTrendMap = computed(() => currentTrends.value.reduce(estimatedReducer, {}))
        const daysOfMonth = Array.from({ length: 31 }, (val, i) => i + 1)
        const daysOfMonthStr = daysOfMonth.map(v => v <= 9 ? `0${v}` : v.toString())
        const chartDataOfLastMonth = computed(() => daysOfMonth.map(val => pastTrendMap.value[val]))
        const chartDataOfCurrent = computed(() => daysOfMonth.map((val, idx) => indexOfStartEstimatedCost.value >= idx ? currentTrendMap.value[val] : NaN))
        const chartDataOfEstimated = computed(() => daysOfMonth.map((val, idx) => {
            if (indexOfStartEstimatedCost.value === idx) {
                return currentTrendMap.value[val]
            } else if (indexOfStartEstimatedCost.value < idx) {
                return estimatedTrendMap.value[val]
            } else {
                return NaN
            }
        }))

        const chartJsDataObj = computed(() => {
            return {
                'Last Month': chartDataOfLastMonth.value,
                'Current': chartDataOfCurrent.value,
                'Estimated Cost': chartDataOfEstimated.value
            }
        })

        const chartData = computed(() => {
            return {
                labels: daysOfMonthStr,
                datasets: yKeys.value.map((label, idx) => {
                    const data = chartJsDataObj.value[label]
                    const colorOption = [themeColors.value['secondary'], themeColors.value['primary'], '#d1cdfa']
                    return {
                        label,
                        data,
                        fill: false,
                        borderColor: colorOption[idx],
                        borderWidth: 3,
                        pointRadius: 3,
                        line: 1,
                        pointBorderWidth: 2,
                        pointHoverBorderWidth: 2,
                        pointBorderColor: themeColors.value[colorOption[idx]],
                        pointHoverBorderColor: themeColors.value[colorOption[idx]],
                        pointBackgroundColor: themeColors.value.light,
                        pointHoverBackgroundColor: themeColors.value[colorOption[idx]]
                    }
                })
            }
        })
        const chartOptions = computed(() => {
            return {
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        right: 50
                    }
                },
                annotation: {
                    annotations: [
                        {
                            type: 'line',
                            id: 'line-1',
                            mode: 'vertical',
                            scaleID: 'x-axis-0',
                            value: Number(indexOfStartEstimatedCost.value + 1),
                            borderColor: estimated.value ? themeColors.value.danger : 'transparent',
                            borderWidth: 2,
                            borderDash: [3]
                        }
                    ]
                },
                plugins: {
                    datalabels: {
                        display: false
                    }
                },
                // responsive: true,
                legend: {
                    display: false
                },
                tooltips: {
                    displayColors: true,
                    mode: 'index',
                    shadowOffsetX: 1,
                    shadowOffsetY: 1,
                    shadowBlur: 8,
                    callbacks: {
                        label: (tooltipItem, data) => {
                            const dataName = data.datasets[tooltipItem.datasetIndex].label
                            const dataValue = vm.$_getDollar(tooltipItem.value, 2)
                            if (indexOfStartEstimatedCost.value === tooltipItem.index && tooltipItem.datasetIndex === 2) return
                            return ` ${dataName} : ${dataValue}`
                        }
                    }
                },
                scales: {
                    xAxes: [
                        {
                            display: true,
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                beginAtZero: true,
                                maxTicksLimit: 15,
                                maxRotation: 0,
                                callback: value => formattedDate(lang.value, value, 'day')
                            }
                        }
                    ],
                    yAxes: [
                        {
                            display: true,
                            ticks: {
                                beginAtZero: true,
                                count: 5,
                                maxTicksLimit: 5,
                                callback: getYData
                            },
                            scaleLabel: {
                                display: true
                            }
                        }
                    ]
                }
            }
        })
        const getYData = (label) => __toKFormat(label)
        const transparentize = (value, opacity) => {
            const r = parseInt(value.slice(1, 3), 16)
            const g = parseInt(value.slice(3, 5), 16)
            const b = parseInt(value.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        }

        return {
            chartData,
            chartOptions,
            getYData,
            transparentize,
            costOfAfterCurrentMonth,
            costOfAfterPastMonth,
            __toKFormat,
            yKeys
        }
    }
}
</script>

<style>

</style>
